import { number, object, string } from 'yup';
import { CycleSettingAction } from '../../enums/action';
import { PostWeldActions } from '../../types/cycle-settings-types';
import i18n from '../../infrastructure/i18n';
import { LATIN_ALPHANUMERIC_WITH_SPACES_REGEX } from '../../constants/common';

export const POST_WELD_SETTINGS_LIMITS = {
  RECIPE_NAME: {
    MIN: 1,
    MAX: 18,
  },
  RATE_UPPER_TOLERANCE: {
    MIN: 0,
    MAX: 1000,
  },
  RATE_LOWER_TOLERANCE: {
    MIN: 0,
    MAX: 1000,
  },
  HOLD_UPPER_TOLERANCE: {
    MIN: 0,
    MAX: 1000,
  },
  HOLD_LOWER_TOLERANCE: {
    MIN: 0,
    MAX: 1000,
  },

};

export const initialSettingsSchema = object().shape({
  name: string()
    .required(i18n.t('errors.requiredValue'))
    .min(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN,
      i18n.t('errors.stringValue.min', '', { maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MIN, value: i18n.t('cycle.settings.postWeld.recipeName') }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX,
      i18n.t('errors.stringValue.max', '', { maxChars: POST_WELD_SETTINGS_LIMITS.RECIPE_NAME.MAX, value: i18n.t('cycle.settings.postWeld.recipeName') }),
    )
    .matches(LATIN_ALPHANUMERIC_WITH_SPACES_REGEX, i18n.t('errors.stringValue.sanitizeAlphaNumericChars')),
  rateUpperTolerance: number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.RATE_UPPER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  rateLowerTolerance: number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.RATE_LOWER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  holdUpperTolerance: number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.HOLD_UPPER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
  holdLowerTolerance: number()
    .typeError(i18n.t('errors.numberValue.typeError', '', { type: 'number' }))
    .min(
      POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MIN,
      i18n.t('errors.numberValue.min', '', { minNum: POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MIN }),
    )
    .max(
      POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MAX,
      i18n.t('errors.numberValue.max', '', { maxNum: POST_WELD_SETTINGS_LIMITS.HOLD_LOWER_TOLERANCE.MAX }),
    )
    .required(i18n.t('errors.requiredValue')),
});

export const actions: PostWeldActions[] = [
  {
    inputType: 'text',
    actionName: CycleSettingAction.SET_RECIPE_NAME,
    actionField: 'recipeName',
    labelText: i18n.t('cycle.settings.postWeld.recipeName'),
    editableKey: 'name',
    validationSchema: object({
      recipeName: initialSettingsSchema.fields.name,
    }),
  },
  {
    inputType: 'number',
    actionName: CycleSettingAction.SET_RECIPE_RATE_UPPER_TOLERANCE,
    actionField: 'upperRampTolerance',
    labelText: i18n.t('cycle.settings.postWeld.upperRampTolerance'),
    editableKey: 'rateUpperTolerance',
    validationSchema: object({
      upperRampTolerance: initialSettingsSchema.fields.rateUpperTolerance,
    }),
  },
  {
    inputType: 'number',
    actionName: CycleSettingAction.SET_RECIPE_RATE_LOWER_TOLERANCE,
    actionField: 'lowerRampTolerance',
    labelText: i18n.t('cycle.settings.postWeld.lowerRampTolerance'),
    editableKey: 'rateLowerTolerance',
    validationSchema: object({
      lowerRampTolerance: initialSettingsSchema.fields.rateLowerTolerance,
    }),
  },
  {
    inputType: 'number',
    actionName: CycleSettingAction.SET_RECIPE_HOLD_UPPER_TOLERANCE,
    actionField: 'upperHoldTolerance',
    labelText: i18n.t('cycle.settings.postWeld.upperHoldTolerance'),
    editableKey: 'holdUpperTolerance',
    validationSchema: object({
      upperHoldTolerance: initialSettingsSchema.fields.holdUpperTolerance,
    }),
  },
  {
    inputType: 'number',
    actionName: CycleSettingAction.SET_RECIPE_HOLD_LOWER_TOLERANCE,
    actionField: 'lowerHoldTolerance',
    labelText: i18n.t('cycle.settings.postWeld.lowerHoldTolerance'),
    editableKey: 'holdLowerTolerance',
    validationSchema: object({
      lowerHoldTolerance: initialSettingsSchema.fields.holdLowerTolerance,
    }),
  },
];

export const settingActions = [
  {
    labelText: i18n.t('cycle.settings.postWeld.recipeName'),
    fieldName: 'name',
  },
  {
    labelText: i18n.t('cycle.settings.mode'),
    fieldName: 'mode',
  },
  {
    labelText: i18n.t('cycle.settings.postWeld.upperRamp'),
    fieldName: 'upperRampTolerance',
  },
  {
    labelText: i18n.t('cycle.settings.postWeld.lowerRamp'),
    fieldName: 'lowerRampTolerance',
  },
  {
    labelText: i18n.t('cycle.settings.postWeld.upperHold'),
    fieldName: 'upperHoldTolerance',
  },
  {
    labelText: i18n.t('cycle.settings.postWeld.lowerHold'),
    fieldName: 'lowerHoldTolerance',
  },
  {
    labelText: i18n.t('cycle.settings.postWeld.hasDifferences'),
    fieldName: 'hasDifferences',
  },
];
